import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  addAppDependency,
  appDependenciesQuery,
  describeAppDependency,
  removeAppDependency,
  type AppDependenciesPage,
  type DependencyDirection,
  type DependencyLine,
  type DependencySuggestion,
} from '../../api/appDependencies'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { appUrl, CriticalityPill, failed } from '../parts'

/**
 * Pe fisa aplicatiei: DEPENDENTELE INTRE APLICATII.
 *
 *  - "Depends on applications": Opera PMS -> Charisma ERP, cu ce trece intre
 *    ele ("night audit export");
 *  - "Used by applications": invers;
 *  - impactul, intr-o propozitie: "If Charisma ERP goes down, these
 *    applications are affected: Opera PMS" - si prin alta aplicatie;
 *  - sugestiile: aplicatii ale caror servere vorbesc cu ale ei (muchii
 *    `connects_to` din harta ADM). Numai sugestii - nu se scrie nimic pana
 *    nu se apasa "Add".
 *
 * Legatura e muchia `depends_on` dintre CI-urile aplicatiilor, deci analiza
 * de impact a CMDB-ului o urmeaza fara nimic in plus. Inainte de migrare sau
 * fara drept, cardul nu apare.
 */
export function AppDependencies({ appId }: { appId: number }) {
  const page = useQuery({ ...appDependenciesQuery(appId), retry: false })
  const [adding, setAdding] = useState<{ direction: DependencyDirection; otherId: number; origin: 'manual' | 'suggested' } | null>(null)
  const query = useQueryClient()

  const data = page.data
  if (!data?.ready || data.dependsOn === undefined) return null
  const reload = () => void query.invalidateQueries({ queryKey: ['business-apps'] })
  const canEdit = data.canEdit === true
  const suggestions = data.suggestions ?? []
  const affected = data.impact?.apps ?? []

  return (
    <>
      <div className="adm-section">
        <h4>Linked applications</h4>
        <span className="adm-section-note">what it needs from other applications, and who needs it</span>
        {canEdit && (data.options ?? []).length > 0 && (
          <button
            type="button"
            id="appDepAdd"
            className="btn btn-default btn-xs"
            style={{ marginLeft: 'auto' }}
            onClick={() => setAdding({ direction: 'depends_on', otherId: 0, origin: 'manual' })}
          >
            <i className="fa fa-link"></i> Add link
          </button>
        )}
      </div>
      <div className="adm-card" id="appDependencies">
        <div id="appDepImpact" className={affected.length > 0 ? 'alert alert-warning' : 'text-muted'} style={{ margin: '10px 15px' }}>
          {affected.length > 0 && <i className="fas fa-exclamation-triangle"></i>} {data.impact?.sentence}
          {affected.some((a) => !a.direct) && (
            <div className="small" style={{ marginTop: 4 }}>
              {affected
                .filter((a) => !a.direct)
                .map((a) => `${a.name} through ${a.via.join(' and ')}`)
                .join('; ')}
            </div>
          )}
        </div>

        <Lines
          id="appDepDependsOn"
          title="Depends on applications"
          empty="It does not depend on another recorded application."
          lines={data.dependsOn}
          canEdit={canEdit}
          onChanged={reload}
        />
        <Lines
          id="appDepUsedBy"
          title="Used by applications"
          empty="No other application is recorded as using it."
          lines={data.usedBy ?? []}
          canEdit={canEdit}
          onChanged={reload}
        />

        {suggestions.length > 0 && (
          <div id="appDepSuggestions" style={{ padding: '6px 15px 10px' }}>
            <div className="adm-muted small" style={{ marginBottom: 4 }}>
              <i className="fas fa-lightbulb"></i> Suggested from the connections between their servers - not linked until you add them
            </div>
            <table className="table adm-list">
              <tbody>
                {suggestions.map((s) => (
                  <SuggestionRow
                    key={`${s.direction}-${s.appId}`}
                    s={s}
                    name={data.name ?? ''}
                    onAdd={() => setAdding({ direction: s.direction, otherId: s.appId, origin: 'suggested' })}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data.cmdb && !data.cmdb.inCmdb && (
          <p className="adm-muted small" style={{ padding: '0 15px 10px' }}>
            This application is not in the CMDB yet, so the CMDB impact analysis does not follow its links.
          </p>
        )}
      </div>
      {adding && (
        <AddModal
          page={data}
          initial={adding}
          onClose={() => setAdding(null)}
          onSaved={() => {
            setAdding(null)
            reload()
          }}
        />
      )}
    </>
  )
}

function Lines({
  id,
  title,
  empty,
  lines,
  canEdit,
  onChanged,
}: {
  id: string
  title: string
  empty: string
  lines: DependencyLine[]
  canEdit: boolean
  onChanged(): void
}) {
  const describe = (line: DependencyLine) => {
    const text = window.prompt(`What passes between the two applications (for example "night audit export")?`, line.description)
    if (text === null) return
    describeAppDependency(line.id, text)
      .then((r) => {
        toast(r.message, 'Applications', r.status === 'OK' ? 'success' : 'error')
        if (r.status === 'OK') onChanged()
      })
      .catch((error) => toast(failed(error), 'Applications', 'error'))
  }
  const remove = (line: DependencyLine) => {
    if (!window.confirm(`Remove the link with ${line.name}?`)) return
    removeAppDependency(line.id)
      .then((r) => {
        toast(r.message, 'Applications', r.status === 'OK' ? 'success' : 'error')
        if (r.status === 'OK') onChanged()
      })
      .catch((error) => toast(failed(error), 'Applications', 'error'))
  }

  return (
    <div id={id} style={{ padding: '4px 15px 6px' }}>
      <div className="adm-muted small" style={{ fontWeight: 600, textTransform: 'uppercase' }}>
        {title}
      </div>
      {lines.length === 0 ? (
        <p className="text-muted small" style={{ margin: '4px 0 6px' }}>
          {empty}
        </p>
      ) : (
        <table className="table adm-list">
          <tbody>
            {lines.map((l) => (
              <tr key={l.id}>
                <td>
                  <a href={appUrl(l.appId)} target="_blank" rel="noopener noreferrer">{l.name}</a>
                  {l.description !== '' && <div className="adm-muted small">{l.description}</div>}
                  {!l.inCmdb && <div className="adm-muted small">not followed by the CMDB impact analysis (no CI)</div>}
                </td>
                <td className="adm-nowrap">
                  <CriticalityPill value={l.criticality} label={l.criticalityLabel} />
                </td>
                {canEdit && (
                  <td className="adm-nowrap text-right">
                    <button type="button" className="btn btn-default btn-xs" title="What passes between them" onClick={() => describe(l)}>
                      <i className="fa fa-pencil-alt"></i>
                    </button>{' '}
                    <button type="button" className="btn btn-default btn-xs" title="Remove the link" onClick={() => remove(l)}>
                      <i className="fa fa-unlink"></i>
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function SuggestionRow({ s, name, onAdd }: { s: DependencySuggestion; name: string; onAdd(): void }) {
  return (
    <tr>
      <td>
        {s.direction === 'depends_on' ? (
          <>
            {name} may depend on <strong>{s.name}</strong>
          </>
        ) : (
          <>
            <strong>{s.name}</strong> may depend on {name}
          </>
        )}
        <div className="adm-muted small">{s.evidence}</div>
      </td>
      <td className="adm-nowrap text-right">
        <button type="button" className="btn btn-default btn-xs" onClick={onAdd}>
          <i className="fa fa-plus"></i> Add
        </button>
      </td>
    </tr>
  )
}

function AddModal({
  page,
  initial,
  onClose,
  onSaved,
}: {
  page: AppDependenciesPage
  initial: { direction: DependencyDirection; otherId: number; origin: 'manual' | 'suggested' }
  onClose(): void
  onSaved(): void
}) {
  const [direction, setDirection] = useState<DependencyDirection>(initial.direction)
  const [otherId, setOtherId] = useState(initial.otherId)
  const [description, setDescription] = useState('')
  const [busy, setBusy] = useState(false)
  const options = (page.options ?? []).filter((o) => (direction === 'depends_on' ? o.canDependOn : o.canBeUsedBy))
  const chosen = options.find((o) => o.appId === otherId)

  const save = () => {
    if (!chosen) return
    setBusy(true)
    addAppDependency(page.appId ?? 0, { other_app_id: chosen.appId, direction, description, origin: initial.origin })
      .then((r) => {
        toast(r.message, 'Applications', r.status === 'OK' ? 'success' : 'error')
        if (r.status === 'OK') onSaved()
      })
      .catch((error) => toast(failed(error), 'Applications', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <Modal
      id={`appDepModal-${page.appId ?? 0}`}
      open
      onClose={onClose}
      title="Link to another application"
      footer={
        <>
          <button type="button" className="btn btn-default" onClick={onClose}>
            Cancel
          </button>
          <button type="button" id="appDepSave" className="btn btn-primary" disabled={!chosen || busy} onClick={save}>
            Add link
          </button>
        </>
      }
    >
      <div className="form-group">
        <label htmlFor="appDepDirection">{page.name}</label>
        <select
          id="appDepDirection"
          className="form-control"
          value={direction}
          onChange={(ev) => setDirection(ev.target.value as DependencyDirection)}
        >
          <option value="depends_on">depends on</option>
          <option value="used_by">is needed by</option>
        </select>
      </div>
      <div className="form-group">
        <label htmlFor="appDepOther">Application</label>
        <select id="appDepOther" className="form-control" value={otherId} onChange={(ev) => setOtherId(Number(ev.target.value))}>
          <option value={0}>- choose -</option>
          {options.map((o) => (
            <option key={o.appId} value={o.appId}>
              {o.name} ({o.criticalityLabel})
            </option>
          ))}
        </select>
      </div>
      <div className="form-group">
        <label htmlFor="appDepDescription">What passes between them</label>
        <input
          id="appDepDescription"
          className="form-control"
          maxLength={255}
          placeholder="for example: night audit export"
          value={description}
          onChange={(ev) => setDescription(ev.target.value)}
        />
      </div>
      {chosen && (
        <p className="adm-muted small" id="appDepPreview">
          {direction === 'depends_on'
            ? `If ${chosen.name} goes down, ${page.name} will show as affected.`
            : `If ${page.name} goes down, ${chosen.name} will show as affected.`}
        </p>
      )}
    </Modal>
  )
}
